import { useCallback, useEffect, useRef, useState } from "react";

// Per-tab persistent state. Behaves like useState, but the value is mirrored
// to sessionStorage under `key` so it survives navigation within the same
// tab (the "smart back navigation" use case — leave a filtered table, drill
// into a row, hit Back, find the filters and sort still applied).
//
// Refreshes within the tab also restore the value. Closing the tab clears it
// — the value is intentionally not durable across sessions so users aren't
// surprised by ancient filters the next morning.
//
// `initial` is read once on mount. Pass a stable reference (or define the
// default outside the component) to avoid hydration churn.
export function useSessionState<T>(
  key: string,
  initial: T,
): readonly [T, (next: T | ((prev: T) => T)) => void, () => void] {
  const [value, setValue] = useState<T>(() => readFromStorage(key, initial));
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // sessionStorage can throw under private-browsing quotas; the in-memory
      // state still works so we silently drop the persistence.
    }
  }, [key, value]);

  const reset = useCallback(() => {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.removeItem(keyRef.current);
    } catch {
      // ignore
    }
    setValue(initial);
  }, [initial]);

  return [value, setValue, reset] as const;
}

function readFromStorage<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
